import { avatarTraits, type AvatarTraits } from "@/lib/defaultAvatar";
import { cn } from "@/lib/utils";

const INK = "#1f2937";

function Eyes({ eyes }: Pick<AvatarTraits, "eyes">) {
  switch (eyes) {
    case "happy":
      return (
        <g fill="none" stroke={INK} strokeWidth={2.6} strokeLinecap="round">
          <path d="M37 47 q4 -5 8 0" />
          <path d="M55 47 q4 -5 8 0" />
        </g>
      );
    case "wink":
      return (
        <g>
          <circle cx={41} cy={46} r={3.4} fill={INK} />
          <path d="M55 46.5 h8" stroke={INK} strokeWidth={2.6} strokeLinecap="round" />
        </g>
      );
    case "sleepy":
      return (
        <g fill="none" stroke={INK} strokeWidth={2.6} strokeLinecap="round">
          <path d="M37 45 q4 4 8 0" />
          <path d="M55 45 q4 4 8 0" />
        </g>
      );
    default:
      return (
        <g>
          <circle cx={41} cy={46} r={3.6} fill={INK} />
          <circle cx={59} cy={46} r={3.6} fill={INK} />
          <circle cx={42.2} cy={44.8} r={1.1} fill="#fff" />
          <circle cx={60.2} cy={44.8} r={1.1} fill="#fff" />
        </g>
      );
  }
}

function Mouth({ mouth }: Pick<AvatarTraits, "mouth">) {
  switch (mouth) {
    case "grin":
      return <path d="M42 55 q8 9 16 0 z" fill={INK} />;
    case "open":
      return <ellipse cx={50} cy={57} rx={3.6} ry={4} fill={INK} />;
    case "flat":
      return <path d="M45 57 h10" stroke={INK} strokeWidth={2.6} strokeLinecap="round" />;
    default:
      return <path d="M43 55 q7 6 14 0" fill="none" stroke={INK} strokeWidth={2.6} strokeLinecap="round" />;
  }
}

function Hat({ hat, hatColor }: Pick<AvatarTraits, "hat" | "hatColor">) {
  switch (hat) {
    case "cap":
      return (
        <g fill={hatColor} stroke={INK} strokeWidth={1.5} strokeLinejoin="round">
          <path d="M27 36 a23 19 0 0 1 46 0 z" />
          <path d="M60 35 h18 a3 3 0 0 1 0 5 h-18 z" />
        </g>
      );
    case "beanie":
      return (
        <g stroke={INK} strokeWidth={1.5}>
          <path d="M27 37 a23 22 0 0 1 46 0 z" fill={hatColor} />
          <rect x={25} y={34} width={50} height={7} rx={3.5} fill={hatColor} />
          <circle cx={50} cy={14} r={4} fill={hatColor} />
        </g>
      );
    case "headband":
      return <rect x={26} y={33} width={48} height={6} rx={3} fill={hatColor} stroke={INK} strokeWidth={1.5} />;
    case "antenna":
      return (
        <g stroke={INK} strokeWidth={1.8}>
          <path d="M50 22 L56 9" fill="none" strokeLinecap="round" />
          <circle cx={57} cy={8} r={4} fill={hatColor} />
        </g>
      );
    default:
      return null;
  }
}

/**
 * The drawn default avatar (see lib/defaultAvatar). Decorative: the Avatar
 * around it carries the name, so this is aria-hidden.
 */
export function GeneratedAvatar({ seed, className }: { seed: string | null | undefined; className?: string }) {
  const t = avatarTraits(seed);
  return (
    <svg
      viewBox="0 0 100 100"
      className={cn("h-full w-full", className)}
      aria-hidden
      focusable="false"
      data-testid="generated-avatar"
    >
      <rect width={100} height={100} fill={t.background} />
      {/* shoulders, then the neck, then the head over both */}
      <ellipse cx={50} cy={102} rx={34} ry={26} fill={t.outfit} stroke={INK} strokeWidth={1.8} />
      <rect x={44} y={64} width={12} height={12} fill={t.skin} stroke={INK} strokeWidth={1.8} />
      <circle cx={50} cy={46} r={24} fill={t.skin} stroke={INK} strokeWidth={1.8} />
      <circle cx={34} cy={53} r={3.2} fill="#fb7185" opacity={0.45} />
      <circle cx={66} cy={53} r={3.2} fill="#fb7185" opacity={0.45} />
      <Eyes eyes={t.eyes} />
      {t.glasses && (
        <g fill="none" stroke={INK} strokeWidth={1.8}>
          <circle cx={41} cy={46} r={6.5} />
          <circle cx={59} cy={46} r={6.5} />
          <path d="M47.5 46 h5" />
        </g>
      )}
      <Mouth mouth={t.mouth} />
      <Hat hat={t.hat} hatColor={t.hatColor} />
    </svg>
  );
}
